import React, { createContext, useContext, ReactNode } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { useProfile } from '@/hooks/useProfile';

/**
 * Admin Context - 관리자 전용 컨텍스트
 * 관리자 권한과 관리 기능에 필요한 모든 데이터 포함
 */
interface AdminContextType {
  user: ReturnType<typeof useAuth>['user'];
  profile: ReturnType<typeof useProfile>['data'];
  isAuthenticated: boolean;
  isAdmin: boolean;
  isLoading: boolean;
  // 관리자 전용 상태들
  adminSettings?: {
    dashboardRefreshInterval: number;
    notificationSettings: {
      emailEnabled: boolean;
      smsEnabled: boolean;
    };
  };
}

const AdminContext = createContext<AdminContextType | undefined>(undefined);

// eslint-disable-next-line react-refresh/only-export-components
export const useAdminContext = () => {
  const context = useContext(AdminContext);
  if (context === undefined) {
    throw new Error('useAdminContext must be used within an AdminProvider');
  }
  return context;
};

interface AdminProviderProps {
  children: ReactNode;
}

/**
 * AdminProvider - 관리자 전용 컨텍스트 제공자
 * 관리자 권한 확인 및 관리 기능에 필요한 데이터 관리
 * 실제 접근 제어는 AdminProtectedRoute에서 처리하므로 여기서는 컨텍스트만 제공
 */
export const AdminProvider: React.FC<AdminProviderProps> = ({ children }) => {
  const { user, loading } = useAuth();
  const { data: profile } = useProfile(user?.id || null);

  const isAdmin = profile?.role === 'admin';

  // AdminProvider는 컨텍스트만 제공하고, 실제 접근 제어는 AdminProtectedRoute에서 처리
  // 이렇게 하면 로그인 페이지(/admin/login)에서도 AdminProvider를 사용할 수 있음
  const value: AdminContextType = {
    user,
    profile,
    isAuthenticated: !!user,
    isAdmin,
    isLoading: loading,
    adminSettings: {
      dashboardRefreshInterval: 30000, // 30초
      notificationSettings: {
        emailEnabled: true,
        smsEnabled: false,
      },
    },
  };

  return <AdminContext.Provider value={value}>{children}</AdminContext.Provider>;
};

export default AdminProvider;